import { CountryCodes, Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import type { IUISupplier } from '@bridebook/toolbox/src/types';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { selectMarketWithFallback } from '@bridebook/web/lib/i18n/selectors';
import { getFilters } from '@bridebook/web/lib/search/selectors';
import type { SearchResponseFields } from '@bridebook/web/lib/search/types';
import type { IApplicationState } from '@bridebook/web/lib/types';
import { buildAnalyticsActiveFilters } from './build-active-filters';

export const buildViewedSupplierInSearchEvent = (
  supplier: IUISupplier,
  state: IApplicationState,
  pagePosition: number,
  searchPosition: number,
) => {
  const {
    search: {
      searchSource,
      mobileSearchViewAnalytics,
      onboardingFilters,
      preset,
      results,
      request,
    },
    weddings: { profile: weddingProfile },
    app: { previousPath },
    users: { user },
  } = state;

  const isDesktop = getScreenType(state).isDesktop;
  const isMobile = getScreenType(state).isMobile;

  const searchView = isDesktop ? '' : mobileSearchViewAnalytics;

  const fields: SearchResponseFields | null = results.status === 'loaded' ? results.fields : null;
  const market = selectMarketWithFallback(state);
  const countryCode = (fields?.countryCode ?? market.country) as CountryCodes;
  const countryName = Gazetteer.getCountryName(countryCode);
  const locale = market.locale;

  const rawFilters = getFilters(state);
  const allActiveFilters = {
    ...buildAnalyticsActiveFilters(rawFilters, fields),
    ...(preset ? { [preset]: { [preset]: true } } : {}),
  };

  const onboardingFiltersIncluded = Object.keys(onboardingFilters).every((key) => {
    if (fields && Object.hasOwn(fields as Record<string, any>, key)) {
      return (
        (fields as Record<string, any>)[key] ===
        (onboardingFilters as Record<string, any>)[key].toString()
      );
    }
    return false;
  });

  const supplierCategory = fields?.type ?? request.slug;
  const isVenueSearch = supplierCategory === 'venue';

  return {
    event: 'Viewed supplier in search',
    budgetInitialTarget: weddingProfile.budget,
    countryCode,
    countryName,
    id: user?.id ?? '',
    supplierId: supplier.id,
    locale,
    mobileApp: isMobile,
    onboardingFilters: searchSource === 'finishedOnboarding' && onboardingFiltersIncluded,
    placeId: fields?.placeId,
    previousPath,
    searchActiveFilters: allActiveFilters,
    searchCoords: {
      swlat: fields?.swlat,
      swlon: fields?.swlon,
      nelat: fields?.nelat,
      nelon: fields?.nelon,
    },
    searchPageNumber: fields?.page ?? request.page,
    searchSortedBy: fields?.sort ?? request.sort,
    searchSource: searchSource || 'newSearch',
    searchTerm: fields?.area ?? request.area,
    searchType: fields?.searchType ?? '',
    searchView,
    supplierCategory,
    supplierCountryCode: countryCode,
    supplierCountryName: countryName,
    weddingId: weddingProfile.id,
    supplierScore: supplier._score ?? 0,
    supplierRegistered: Boolean(supplier.registrationDate),
    supplierTier: supplier.tier ?? 0,
    isForeign: Boolean(supplier.isForeign),
    ...(isVenueSearch
      ? {
          isTopSpotCounty: Boolean(
            supplier.supplierBoosted && supplier.boost && !supplier.isForeign,
          ),
          isTopSpotCountry: supplier.matchedBy?.includes('placeTopSpot') || false,
          isTopSpotVenueType: Boolean(
            supplier.searchBoostForType &&
              rawFilters?.venueType?.[supplier.searchBoostForType] &&
              !supplier.isForeign,
          ),
        }
      : {}),
    pagePosition,
    searchPosition,
    trackingTarget: 'BigQuery',
  };
};
